import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, Observable } from 'rxjs';
import { SvpLockService } from '../../../../svp-detail/services/svp-lock-service/svp-lock.service';
import { isLicenseRefusal, ProblemDetail } from '../../../models/problem-detail.model';
import { ApiErrorMessages, isApiError } from '../../../errors/api-error';
import { ApiErrorCode } from '../../../errors/api-error-code';
import { ApiErrorHandler } from '../../../errors/api-error-handler';

/**
 * What a dialog should do with a mutation it ran itself.
 *
 * `failed` is the only outcome that keeps the dialog open: the user can fix the input (or just
 * try again) without retyping the form. `aborted` covers refusals that are already explained
 * application-wide — a locked ŠVP, an expired licence — where retrying in the dialog cannot
 * succeed, so the dialog closes and lets that explanation stand. `committed` is a write that went
 * through although a follow-up step failed (`INVITATION_SEND_FAILED`: the account exists, its
 * invitation did not go out); the API's explanation has been shown and the dialog closes as saved,
 * since submitting the form again would only be refused as a duplicate.
 */
type ProblemField = NonNullable<ProblemDetail['fields']>[number];

export type DialogMutationResult<T> =
  | { outcome: 'success'; value: T }
  /**
   * `code` is the API's, for the rare dialog that decides what happens next by it; `fields` are the
   * fields a `VALIDATION_FAILED` rejected, for a dialog that marks them.
   */
  | { outcome: 'failed'; message: string; code?: string; fields?: ProblemField[] }
  | { outcome: 'committed' }
  | { outcome: 'aborted' };

/**
 * Czech messages a call site wants shown instead of the API's own `detail`, keyed by code — for
 * the dialog that can say it better ("použijte Přidat existujícího uživatele") than the server can.
 */
export type DialogMutationMessages = ApiErrorMessages;

/**
 * Runs a dialog's own mutation and classifies its failure into "show it in the dialog" versus
 * "close, it is explained elsewhere".
 *
 * This exists so a dialog owning its action does not have to re-implement the lock-conflict and
 * licence-refusal branches that every data service carries.
 */
@Injectable({
  providedIn: 'root',
})
export class DialogMutationService {
  private readonly svpLockService = inject(SvpLockService);
  private readonly apiErrors = inject(ApiErrorHandler);

  /**
   * @param source the mutation, unsubscribed — it is subscribed here and completed on the first value
   * @param context Sentry context, e.g. `'SubjectDialog.createSubject'`
   * @param messages Czech messages to show instead of the API's `detail`, keyed by code. Whatever
   *   the API refuses with is shown — its detail is written for the user — and only a failure it
   *   did not explain (or an internal error) becomes the generic message. Only a failure the API
   *   never answered is reported to Sentry; a coded response the backend has already captured.
   */
  async run<T>(
    source: Observable<T>,
    context: string,
    messages: DialogMutationMessages = {},
  ): Promise<DialogMutationResult<T>> {
    try {
      return { outcome: 'success', value: await firstValueFrom(source) };
    } catch (error) {
      return this.classify(error, context, messages);
    }
  }

  private classify<T>(
    error: unknown,
    context: string,
    messages: DialogMutationMessages,
  ): DialogMutationResult<T> {
    if (!(error instanceof HttpErrorResponse)) {
      return { outcome: 'failed', message: this.apiErrors.describe(error, context).message };
    }

    if (this.svpLockService.isLockConflict(error)) {
      this.svpLockService.handleLockConflict(error);
      return { outcome: 'aborted' };
    }

    // The licence interceptor has already shown its own message for these.
    if (isLicenseRefusal(error)) {
      return { outcome: 'aborted' };
    }

    if (isApiError(error, ApiErrorCode.INVITATION_SEND_FAILED)) {
      this.apiErrors.notify(error, context);
      return { outcome: 'committed' };
    }

    const { code, message } = this.apiErrors.describe(error, context, messages);
    const fields = (error.error as ProblemDetail | null)?.fields;
    return { outcome: 'failed', message, code, ...(fields?.length && { fields }) };
  }
}
